const ADMIN_API_BASE = '/api/admin/module'
const LANDING_ADMIN_API_BASE = '/api/admin/landing'
const DEV_BACKUP_API_BASE = '/api/dev/production-backup'

const request = async (url, options = {}) => {
  const response = await fetch(url, { credentials: 'include', ...options })
  let payload = null
  try {
    payload = await response.json()
  } catch {
    payload = null
  }

  if (!response.ok) {
    const error = new Error(payload?.message || `HTTP ${response.status}`)
    error.response = payload
    throw error
  }

  return payload
}

const toQueryString = (params = {}) => {
  const query = new URLSearchParams()

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return
    }

    query.append(key, String(value))
  })

  const suffix = query.toString()
  return suffix ? `?${suffix}` : ''
}

export const adminService = {
  getCategories: () => request('/api/admin/categories'),
  getProducts: () => request('/api/admin/products'),

  getUsers: (activeOnly = null) => {
    const query = activeOnly === null ? '' : `?activeOnly=${activeOnly}`
    return request(`${ADMIN_API_BASE}/users${query}`)
  },
  getRoles: () => request(`${ADMIN_API_BASE}/users/roles`),
  updateUserActive: (userId, isActive) => request(`${ADMIN_API_BASE}/users/${userId}/active`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ isActive })
  }),
  updateUserRoles: (userId, roles) => request(`${ADMIN_API_BASE}/users/${userId}/roles`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ roles })
  }),
  updateUserProfile: (userId, profile) => request(`${ADMIN_API_BASE}/users/${userId}/profile`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(profile)
  }),

  getLicenses: ({ userId, status } = {}) => {
    const query = new URLSearchParams()
    if (userId) query.append('userId', userId)
    if (status) query.append('status', status)
    const suffix = query.toString() ? `?${query.toString()}` : ''
    return request(`${ADMIN_API_BASE}/licenses${suffix}`)
  },
  getLicenseProducts: () => request(`${ADMIN_API_BASE}/licenses/products`),
  getLicenseRequests: ({ status } = {}) => {
    const query = new URLSearchParams()
    if (status) query.append('status', status)
    const suffix = query.toString() ? `?${query.toString()}` : ''
    return request(`${ADMIN_API_BASE}/licenses/requests${suffix}`)
  },
  assignLicense: (data) => request(`${ADMIN_API_BASE}/licenses`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  }),
  approveLicenseRequest: (requestId, data = {}) => request(`${ADMIN_API_BASE}/licenses/requests/${requestId}/approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  }),
  deactivateLicense: (licenseId) => request(`${ADMIN_API_BASE}/licenses/${licenseId}/deactivate`, {
    method: 'PUT'
  }),
  payLicense: (licenseId, data = {}) => request(`${ADMIN_API_BASE}/licenses/${licenseId}/payments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  }),
  ensureRentalBizProfile: (userId) => request(`${ADMIN_API_BASE}/users/${userId}/rentalbiz-profile`, {
    method: 'POST'
  }),

  getSchemaStatus: () => request(`${ADMIN_API_BASE}/schema/status`),
  applySchemaMigrations: (moduleKey, onlyNext = false) =>
    request(`${ADMIN_API_BASE}/schema/${moduleKey}/migrate?onlyNext=${onlyNext}`, {
      method: 'POST'
    }),

  getLandingPages: (params = {}) =>
    request(`${LANDING_ADMIN_API_BASE}/pages${toQueryString(params)}`),
  createLandingPage: (data) =>
    request(`${LANDING_ADMIN_API_BASE}/pages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    }),
  updateLandingPage: (id, data) =>
    request(`${LANDING_ADMIN_API_BASE}/pages/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    }),
  getLandingLeads: (params = {}) =>
    request(`${LANDING_ADMIN_API_BASE}/leads${toQueryString(params)}`),
  getLandingVisits: (params = {}) =>
    request(`${LANDING_ADMIN_API_BASE}/visits${toQueryString(params)}`),
  getLandingEvents: (params = {}) =>
    request(`${LANDING_ADMIN_API_BASE}/events${toQueryString(params)}`),
  getLandingAttributions: (params = {}) =>
    request(`${LANDING_ADMIN_API_BASE}/attributions${toQueryString(params)}`),

  createProductionBackupRequest: () => request('/api/admin/production-backups/requests', { method: 'POST' }),
  getProductionBackupRequestStatus: (requestId) => request(`/api/admin/production-backups/requests/${requestId}/status`),
  getProductionBackupAvailability: () => request(`${DEV_BACKUP_API_BASE}/availability`),
  startProductionBackupImport: (payload) => request(`${DEV_BACKUP_API_BASE}/import`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  }),
  getProductionBackupImportStatus: (jobId) => request(`${DEV_BACKUP_API_BASE}/import/${jobId}/status`)
}
